import { useId } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import clsx from 'clsx'

import { isString } from 'shared/utils/common'
import { emptyError } from 'shared/utils/emptyError'

import styles from './ControlledCheckbox.module.scss'

interface ControlledCheckboxProps {
  name: string
  value?: boolean
  label: string | React.ReactNode
  defaultValue?: boolean | null
  disabled?: boolean | null
  required?: boolean
  noMargin?: boolean
  paddingLeft?: boolean
  id?: string
}

export function ControlledCheckbox({
  label,
  id,
  name,
  disabled,
  required,
  noMargin,
  paddingLeft
}: ControlledCheckboxProps) {
  const {
    control,
    formState: { errors }
  } = useFormContext()
  const { message: errorMessage } = errors?.[name] || emptyError()
  const generatedId = useId()
  const inputId = id || `controlled-checkbox-${generatedId}`
  const hasError = isString(errorMessage) && errorMessage !== ''

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div
          className={clsx(styles.wrapper, {
            [styles.noMargin]: noMargin,
            [styles.paddingLeft]: paddingLeft
          })}
        >
          <label
            htmlFor={inputId}
            className={clsx(styles.label, {
              [styles.labelDisabled]: disabled,
              [styles.labelError]: hasError
            })}
          >
            {required ? (
              <div style={{ display: 'inline-block' }}>
                {label}
                <span className={styles.requiredStar} aria-hidden='true'>
                  &nbsp;*
                </span>
              </div>
            ) : (
              label
            )}
            <input
              {...field}
              id={inputId}
              type='checkbox'
              disabled={disabled ?? false}
              checked={!!field.value}
              required={required}
              aria-invalid={hasError || undefined}
              className={styles.input}
            />
            <span className={styles.mark} aria-hidden='true' />
          </label>
        </div>
      )}
    />
  )
}
